import React from 'react';
import { Card, Typography, Space, message, Form, Input, Button, Alert } from 'antd';
import { useMutation } from '@apollo/client';
import { RESET_ADMIN_USER_PASSWORD } from '../graphql/mutations';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;

const UserSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [resetAdminPassword, { loading: changingPassword }] = useMutation(RESET_ADMIN_USER_PASSWORD);
  const [passwordForm] = Form.useForm();

  const handlePasswordChange = async (values: {
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (!user?.id) {
      message.error('User information not available');
      return;
    }

    try {
      const { data } = await resetAdminPassword({
        variables: {
          id: user.id,
          newPassword: values.newPassword
        }
      });
      
      if (data?.resetAdminUserPassword?.success) {
        message.success(t('settings.passwordChangedSuccess') || 'Password reset successfully. You will be logged out.');
        passwordForm.resetFields();
        
        // Logout user after successful password reset
        setTimeout(() => {
          logout();
        }, 2000); // Give user time to see the success message
      } else {
        message.error(data?.resetAdminUserPassword?.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      message.error(error.message || 'Failed to change password');
    }
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2}>Account Settings</Title>
          <Text type="secondary">
            Manage your admin account settings and reset your password.
          </Text>
        </div>
        
        {user && (
          <Alert
            message="Account Information"
            description={
              <div>
                <Text strong>Email: </Text>
                <Text>{user.email}</Text>
                <br />
                <Text strong>Name: </Text>
                <Text>{user.firstname} {user.lastname}</Text>
                <br />
                <Text strong>Role: </Text>
                <Text>{user.userRole?.name || 'Admin User'}</Text>
              </div>
            }
            type="info"
            style={{ marginBottom: 24 }}
          />
        )}

        <Card title="Reset Password" style={{ maxWidth: 600 }}>
          <Alert
            message="Password Reset"
            description="This will reset your admin password. You will be automatically logged out after the reset and will need to log in again with your new password."
            type="warning"
            style={{ marginBottom: 24 }}
          />
          
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordChange}
            requiredMark={false}
          >
            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[
                { required: true, message: 'Please enter a new password' },
                { min: 8, message: 'Password must be at least 8 characters long' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
                  message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
                }
              ]}
              hasFeedback
            >
              <Input.Password 
                placeholder="Enter your new password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password 
                placeholder="Confirm your new password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 24 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={changingPassword}
                size="large"
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </Card>
  );
};

export default UserSettings;
